import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Send as SendIcon,
  CheckCircle as AcceptIcon,
  Receipt as ConvertIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { formatCurrency as formatCurrencyUtil } from '../../utils/currency';

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Fetch quote details
  const { data: quote, isLoading, error } = useQuery({
    queryKey: ['quote', id],
    queryFn: async () => {
      const response = await apiClient.get(`/financial/quotes/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  const formatCurrency = (amount) => formatCurrencyUtil(amount);

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      // Handle both timestamp and string dates
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // Try parsing as string date
        return dateString;
      }
      return date.toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'accepted':
        return 'success';
      case 'published':
      case 'sent':
        return 'info';
      case 'viewed':
        return 'warning';
      case 'expired':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const calculateDaysRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    try {
      const expiry = new Date(expiryDate);
      const today = new Date();
      const diffTime = expiry - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load quote details. Please try again later.
        </Alert>
        <Button onClick={() => navigate('/financial/quotes')} startIcon={<BackIcon />} sx={{ mt: 2 }}>
          Back to Quotes
        </Button>
      </Box>
    );
  }

  const daysRemaining = calculateDaysRemaining(quote?.valid_until || quote?.expiration_date);

  // For now, show basic quote data since we don't have the detail endpoint
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/financial/quotes')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">
              {quote?.quote_name || `Quote #${quote?.quote_id || id?.slice(-6)}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created {formatDate(quote?.created_date || quote?.created_at)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<EditIcon />} variant="outlined">
            Edit
          </Button>
          <Button startIcon={<SendIcon />} variant="outlined">
            Send
          </Button>
          <Button startIcon={<PrintIcon />} variant="outlined">
            Print
          </Button>
          <Button startIcon={<DownloadIcon />} variant="outlined">
            Download
          </Button>
          {quote?.status !== 'accepted' && (
            <Button startIcon={<ConvertIcon />} variant="contained" color="primary">
              Convert to Invoice
            </Button>
          )}
        </Box>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quote Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Quote Number
                </Typography>
                <Typography variant="body1">
                  {quote?.quote_number || quote?.quote_id || `QUO-${id?.slice(-6)}`}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={quote?.status || 'Draft'}
                  color={getStatusColor(quote?.status)}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Issue Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(quote?.issue_date || quote?.created_date)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Valid Until
                </Typography>
                <Typography variant="body1">
                  {formatDate(quote?.valid_until || quote?.expiration_date)}
                  {daysRemaining !== null && (
                    <Typography variant="caption" color={daysRemaining > 0 ? "text.secondary" : "error"} display="block">
                      {daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Expired'}
                    </Typography>
                  )}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Client
                </Typography>
                <Typography variant="body1">
                  {quote?.client_name || 'Unknown Client'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Total Amount
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(quote?.total_amount || quote?.quote_total)}
                </Typography>
              </Grid>
            </Grid>

            {/* Note: Line items would go here if we had them */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Quote line items and service details would be displayed here once the backend provides this data.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Quote Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Total Amount:</Typography>
              <Typography variant="h6" color="primary">
                {formatCurrency(quote?.total_amount || quote?.quote_total)}
              </Typography>
            </Box>
            {daysRemaining !== null && daysRemaining > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Time Remaining
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(100, Math.max(0, (daysRemaining / 30) * 100))}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {daysRemaining} days until expiration
                </Typography>
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {quote?.status === 'draft' && (
              <Button fullWidth variant="contained" startIcon={<SendIcon />} sx={{ mb: 1 }}>
                Send to Client
              </Button>
            )}
            {quote?.status === 'sent' && (
              <Button fullWidth variant="outlined" startIcon={<SendIcon />} sx={{ mb: 1 }}>
                Resend Quote
              </Button>
            )}
            {quote?.status !== 'accepted' && (
              <Button fullWidth variant="outlined" startIcon={<AcceptIcon />} sx={{ mb: 1 }}>
                Mark as Accepted
              </Button>
            )}
            <Button fullWidth variant="outlined" startIcon={<ConvertIcon />}>
              Convert to Invoice
            </Button>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Activity Log
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {quote?.email_sent_date && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Email sent
                </Typography>
                <Typography variant="body2">
                  {formatDate(quote.email_sent_date)}
                </Typography>
              </Box>
            )}
            {quote?.date_signed && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Signed
                </Typography>
                <Typography variant="body2">
                  {formatDate(quote.date_signed)}
                </Typography>
              </Box>
            )}
            {!quote?.email_sent_date && !quote?.date_signed && (
              <Typography variant="body2" color="text.secondary">
                No activity recorded yet.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default QuoteDetail;